import type { ExpressionSpecification, LineLayerSpecification } from 'mapbox-gl'

export const TILESET_DRAW_ORDER: ExpressionSpecification = [
  'number',
  ['get', 'drawOrder'],
  0,
]

export const TILESET_FILL_OPACITY: ExpressionSpecification = [
  'min',
  1,
  ['max', 0, ['number', ['get', 'fill-opacity'], 0.3]],
]

function parseDashPattern(value: unknown): number[] | undefined {
  if (typeof value !== 'string' || !value.trim()) {
    return undefined
  }
  const lengths: number[] = []
  let unit: string | undefined
  for (const part of value.trim().split(/\s+/)) {
    const match = /^(\d*\.?\d+(?:e[+-]?\d+)?)(g|px|pt|mm|cm|in)?$/i.exec(part)
    if (!match || (unit !== undefined && unit !== (match[2] ?? ''))) {
      return undefined
    }
    const length = Number(match[1])
    if (!Number.isFinite(length)) {
      return undefined
    }
    unit = match[2] ?? ''
    lengths.push(length)
  }
  if (!lengths.some(length => length > 0)) {
    return undefined
  }
  return lengths.length % 2 === 0 ? lengths : [...lengths, ...lengths]
}

export class TilesetLineStyle {
  private readonly patterns = new Map<string, number[]>()

  public constructor(private readonly fallbackWidth: number) {}

  public add(properties: {
    readonly dashPattern?: unknown
    readonly 'stroke-width'?: unknown
  }): boolean {
    if (typeof properties.dashPattern !== 'string') {
      return false
    }
    const rawWidth = properties['stroke-width']
    const width = typeof rawWidth === 'number' ? rawWidth : this.fallbackWidth
    if (!Number.isFinite(width) || width <= 0) {
      return false
    }
    const key = `${properties.dashPattern}:${width}`
    if (this.patterns.has(key)) {
      return false
    }
    const lengths = parseDashPattern(properties.dashPattern)
    if (!lengths) {
      return false
    }
    const shortest = Math.min(...lengths.filter(length => length > 0))
    const minimum = Math.max(2, 2 / width)
    const pattern = lengths.map(length =>
      Math.max(2 / width, (length / shortest) * minimum),
    )
    if (!pattern.every(Number.isFinite)) {
      return false
    }
    this.patterns.set(key, pattern)
    return true
  }

  public get paint(): NonNullable<LineLayerSpecification['paint']> {
    const width: ExpressionSpecification = [
      'max',
      0,
      ['number', ['get', 'stroke-width'], this.fallbackWidth],
    ]
    const dashArray: ExpressionSpecification = [
      'match',
      [
        'concat',
        ['string', ['get', 'dashPattern'], ''],
        ':',
        ['to-string', width],
      ],
      ...Array.from(this.patterns).flatMap(([key, pattern]) => [
        key,
        ['literal', pattern],
      ]),
      ['literal', [1, 0]],
    ]
    return {
      'line-width': width,
      'line-dasharray': this.patterns.size ? dashArray : [1, 0],
    }
  }
}
